import { useMutation, UseMutationOptions } from 'react-query';
import {
  cancelPayroll,
  createSupportTicket,
  requestChangePayroll,
  submitRequestOffCyclePayroll,
} from 'services/tickets';
import { queryClient } from 'states/reactQueryClient';

export const useCreateCancelTicket = (
  businessId: string,
  payrollUUID: string,
  options: UseMutationOptions<unknown, unknown, string> = {},
) => {
  const { mutateAsync, mutate, ...rest } = useMutation(
    (reason) => cancelPayroll(businessId, payrollUUID, reason),
    {
      onSuccess: () => queryClient.invalidateQueries(['upcomingPayrolls']),
      ...options,
    },
  );
  return {
    createTicket: mutate,
    createTicketAsync: mutateAsync,
    ...rest,
  };
};

type UseCreateRequestChangeTicketParams = {
  reason: string;
  date?: string;
};
export const useCreateRequestChangeTicket = (
  businessId: string,
  payrollUUID: string,
  options?: UseMutationOptions<
    unknown,
    unknown,
    UseCreateRequestChangeTicketParams
  >,
) => {
  const { mutate, mutateAsync, ...rest } = useMutation<
    unknown,
    unknown,
    UseCreateRequestChangeTicketParams
  >(
    ({ reason, date }) =>
      requestChangePayroll(businessId, reason, date, payrollUUID),
    {
      onSuccess: () => queryClient.invalidateQueries(['upcomingPayrolls']),
      ...options,
    },
  );

  return {
    createChangeTicket: mutate,
    createChangeTicketAsync: mutateAsync,
    ...rest,
  };
};

export const useCreatePayrollPlanChangeTicket = (
  businessId: string,
  options?: UseMutationOptions<unknown, unknown, string>,
) => {
  const { mutate, mutateAsync, ...rest } = useMutation<
    unknown,
    unknown,
    string
  >((detail) => requestChangePayroll(businessId, detail), {
    onSuccess: () => queryClient.invalidateQueries(['upcomingPayrolls']),
    ...options,
  });

  return {
    createChangeTicket: mutate,
    createChangeTicketAsync: mutateAsync,
    ...rest,
  };
};

interface RequestPayrollDataInfo {
  amount: number;
  date: string;
  businessId: string;
  includeTaxes: boolean;
  reason?: string;
}

export const useRequestOffCyclePayroll = (
  mutationProps?: UseMutationOptions<unknown, unknown, RequestPayrollDataInfo>,
) => {
  const { mutateAsync } = useMutation((data: RequestPayrollDataInfo) => {
    const { amount, date, businessId, includeTaxes, reason } = data;
    return submitRequestOffCyclePayroll(
      amount,
      date,
      businessId,
      includeTaxes,
      reason,
    );
  }, mutationProps);

  return {
    requestOffCyclePayroll: mutateAsync,
  };
};

export const useCreateSupportTicket = (businessId: string) => {
  const { mutate, mutateAsync, ...rest } = useMutation((type: string) =>
    createSupportTicket(businessId, type),
  );
  return {
    createSupportTicket: mutate,
    createSupportTicketAsync: mutateAsync,
    ...rest,
  };
};
